import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

/**
 * WorkflowContext
 *
 * Manages cross-page state for the tutor workflow:
 * Student Roster -> Sessions -> Session Planner -> Whiteboard
 *
 * Persists selected student and session context through URL params and localStorage.
 */

const WorkflowContext = createContext(null);

// Workflow step definitions
export const WORKFLOW_STEPS = [
  {
    id: 'student',
    label: 'Student',
    shortLabel: 'Student',
    path: '/teacher/student-roster',
    technique: 'Connection Building',
    description: 'Select a student from your roster',
    helperContent: {
      title: 'Know Your Learner',
      content: `Understanding your student's profile activates personalized teaching strategies.

Why this matters:
- The brain learns best when content feels relevant
- Knowing learning style helps you present information optimally
- Prior knowledge acts as a foundation for new concepts

Review the student's profile, interests, and history before planning your session.`
    }
  },
  {
    id: 'sessions',
    label: 'Sessions',
    shortLabel: 'Sessions',
    path: '/teacher/sessions',
    technique: 'Spaced Retrieval',
    description: 'Review past sessions and insights',
    helperContent: {
      title: 'Review History',
      content: `Reviewing past sessions strengthens the Spaced Retrieval technique.

Why this matters:
- Spaced review strengthens long-term memory
- Past challenges indicate areas needing reinforcement
- What worked before can guide today's approach

Look at recent session summaries to build on prior learning.`
    }
  },
  {
    id: 'plan',
    label: 'Plan',
    shortLabel: 'Plan',
    path: '/teacher/session-planner',
    technique: 'Chunking',
    description: 'Prepare a structured session plan',
    helperContent: {
      title: 'Structured Preparation',
      content: `Planning with the 3-phase structure (Opening/Core/Closing) ensures effective learning.

Why this matters:
- Chunking breaks complex topics into manageable parts
- Clear structure reduces cognitive load
- AI suggestions are grounded in what worked before

Let the AI help you create a personalized plan.`
    }
  },
  {
    id: 'whiteboard',
    label: 'Whiteboard',
    shortLabel: 'Board',
    path: '/teacher/whiteboard',
    technique: 'Peripheral Perception',
    description: 'Conduct your tutoring session',
    helperContent: {
      title: 'Active Learning',
      content: `Visual learning on the whiteboard engages Peripheral Perception.

Why this matters:
- Visual representations bypass working memory limits
- Drawing together creates shared understanding
- The whiteboard captures session progress for later review

Use notes, graphs, and diagrams to make abstract concepts concrete.`
    }
  }
];

// Get step index by id
const getStepIndex = (stepId) => WORKFLOW_STEPS.findIndex(s => s.id === stepId);

// Determine current step from pathname
const getStepFromPath = (pathname) => {
  // Use exact matching to avoid prefix conflicts
  // (e.g., /session-planner was incorrectly matching /sessions with startsWith)
  const step = WORKFLOW_STEPS.find(s => s.path === pathname);
  return step ? step.id : null;
};

// Storage key for localStorage
const STORAGE_KEY = 'lrbrain-workflow-context';

export const WorkflowProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Student context
  const [studentContext, setStudentContext] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.studentContext || null;
      } catch {
        return null;
      }
    }
    return null;
  });

  // Session context
  const [sessionContext, setSessionContext] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.sessionContext || null;
      } catch {
        return null;
      }
    }
    return null;
  });

  // Workflow active state
  const [isWorkflowActive, setIsWorkflowActive] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.isWorkflowActive || false;
      } catch {
        return false;
      }
    }
    return false;
  });

  // Current step derived from path
  const currentStep = getStepFromPath(location.pathname);
  const currentStepIndex = currentStep ? getStepIndex(currentStep) : -1;

  // Save to localStorage whenever context changes
  useEffect(() => {
    const data = {
      studentContext,
      sessionContext,
      isWorkflowActive
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [studentContext, sessionContext, isWorkflowActive]);

  // Read student from URL param on initial load
  useEffect(() => {
    const studentParam = searchParams.get('student');
    if (studentParam && !studentContext) {
      // If we have a student param but no context, we need to fetch
      // For now, just set the uuid - other pages will fetch full context
      setStudentContext({ uuid: studentParam });
      setIsWorkflowActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Select a student and start workflow
  const selectStudent = useCallback((student) => {
    setStudentContext({
      uuid: student.uuid,
      name: student.name || student.display_name || `Student ${student.uuid.substring(0, 8)}`,
      grade: student.grade_year_level || student.grade,
      subjects: student.target_subjects || student.subjects,
      learningStyle: student.learning_style || student.learningStyle,
      // Additional fields for enhanced workflow display
      session_count: student.session_count,
      confidence_score: student.confidence_score,
      assigned_at: student.assigned_at,
      goals: student.goals
    });
    setIsWorkflowActive(true);
  }, []);

  // Select a session
  const selectSession = useCallback((session) => {
    setSessionContext({
      id: session.id,
      title: session.title || session.name,
      date: session.date || session.created_at,
      summary: session.summary
    });
  }, []);

  // Clear workflow context
  const clearWorkflow = useCallback(() => {
    setStudentContext(null);
    setSessionContext(null);
    setIsWorkflowActive(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Navigate to a step with context preserved
  const navigateToStep = useCallback((stepId, options = {}) => {
    const step = WORKFLOW_STEPS.find(s => s.id === stepId);
    if (!step) return;

    let path = step.path;
    const params = new URLSearchParams();

    // Add student param if we have context
    if (studentContext?.uuid) {
      params.set('student', studentContext.uuid);
    }

    // Add session param if relevant and we have context
    if (sessionContext?.id && (stepId === 'plan' || stepId === 'whiteboard')) {
      params.set('session', sessionContext.id);
    }

    // Add any custom params
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    const queryString = params.toString();
    const fullPath = queryString ? `${path}?${queryString}` : path;

    navigate(fullPath, { state: options.state });
  }, [navigate, studentContext, sessionContext]);

  // Check if a step is completed
  const isStepCompleted = useCallback((stepId) => {
    const stepIndex = getStepIndex(stepId);

    // Step is completed if we're past it
    if (currentStepIndex > stepIndex) return true;

    // Special logic for specific steps
    switch (stepId) {
      case 'student':
        return !!studentContext?.uuid;
      case 'sessions':
        return !!sessionContext?.id;
      default:
        return false;
    }
  }, [currentStepIndex, studentContext, sessionContext]);

  // Check if a step is accessible
  const isStepAccessible = useCallback((stepId) => {
    // All steps are accessible - users can jump to any step
    return true;
  }, []);

  const value = {
    // State
    studentContext,
    sessionContext,
    isWorkflowActive,
    currentStep,
    currentStepIndex,

    // Actions
    selectStudent,
    selectSession,
    clearWorkflow,
    navigateToStep,
    setStudentContext,
    setSessionContext,

    // Helpers
    isStepCompleted,
    isStepAccessible,
    steps: WORKFLOW_STEPS
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

// Custom hook to use workflow context
export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

export default WorkflowContext;
